import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Row,
  Modal,
  ModalBody,
  Label,
  ModalHeader,
  ModalFooter,
  FormFeedback,
  Badge,
} from "reactstrap";
import * as moment from "moment";
import '../blog.css';

import BreadCrumb from "../Components/Common/BreadCrumb";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import TableContainerComp from "Components/Common/TableContainerComp";
import { toast } from "react-toastify";
import SpinnerLoader from "Components/Common/Spinner/Spinner";
import useUserStore from "Store/UserStore/UserStore";

// Import a rich text editor - using ReactQuill as an example
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Import API service
import fluentBlogApiServiceObj from "ApiService/fluentBlogApiService";
import { Blog, CreateBlogPayload, UpdateBlogPayload } from "Interface/fluentBlog";

const FluentBlog = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [blogObject, setCurrentBlog] = useState<Blog | null>(null);
  const [pageSize] = useState(10);
  const [blogPageIndex, setBlogPageIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCountOfPage, setTotalCountOfPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [modal, setModal] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState(""); // Separate state for input field
  const [serialNumber, setSerialNumber] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [featureImageFile, setFeatureImageFile] = useState<File | null>(null);
  const [featureImagePreview, setFeatureImagePreview] = useState<string | null>(null);
  const [gridImageFile, setGridImageFile] = useState<File | null>(null);
  const [gridImagePreview, setGridImagePreview] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState<string | null>(null);
  const [faqs, setFaqs] = useState<any[]>([{ question: "", answer: "" }]);
  const [blogContent, setBlogContent] = useState("");
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState("");
  const [categories, setCategories] = useState<string[]>([]);
  const [categoryInput, setCategoryInput] = useState("");
  const [existingCategories, setExistingCategories] = useState<string[]>([]);

  const { currentUser } = useUserStore((state: any) => ({
    currentUser: state.currentUser,
  }));

  // ReactQuill modules configuration WITHOUT image uploader
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ font: [] }],
        [{ size: ["small", false, "large", "huge"] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ script: "sub" }, { script: "super" }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ align: [] }],
        ["blockquote", "code-block"],
        ["link", "image", "video"],
        ["clean"],
      ],
    },
  }), []);

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];

  // Function to generate slug from title
  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  };

  // Handle blog title change to auto-generate slug
  const handleBlogTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    validation.handleChange(e); // Update the formik value
    
    // Auto-generate slug only if we're not in edit mode or if the slug is empty/untouched
    if (!isEditMode || !validation.values.slug || validation.values.slug === generateSlug(validation.values.blogTitle || '')) {
      const generatedSlug = generateSlug(title);
      validation.setFieldValue('slug', generatedSlug);
    }
  };

  // API functions
  const listingBlogs = useCallback(async (filters: any) => {
    try {
      const response = await fluentBlogApiServiceObj.listing(filters);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching blogs:', error);
      throw error;
    }
  }, []);

  const createBlog = async (blog: CreateBlogPayload) => {
    try {
      const response = await fluentBlogApiServiceObj.create(blog);
      return response.data;
    } catch (error: any) {
      console.error('Error creating blog:', error);
      throw error;
    }
  };

  const updateBlog = async (blog: UpdateBlogPayload) => {
    try {
      const response = await fluentBlogApiServiceObj.update(blog);
      return response.data;
    } catch (error: any) {
      console.error('Error updating blog:', error);
      throw error;
    }
  };

  const deleteBlog = async (payload: { _id: string }) => {
    try {
      const response = await fluentBlogApiServiceObj.archive(payload);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting blog:', error);
      throw error;
    }
  };

  const uploadBlogImage = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'blogs');

      const response = await fluentBlogApiServiceObj.uploadImage(formData);
      return response.data;
    } catch (error: any) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  // Function to convert base64 images to uploaded URLs
  const processBlogContentImages = async (content: string): Promise<string> => {
    if (!content) return content;

    // Create a temporary DOM element to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;

    // Find all base64 images
    const base64Images = tempDiv.querySelectorAll('img[src^="data:image/"]');
    
    if (base64Images.length === 0) {
      return content; // No base64 images, return original content
    }

    // Process each base64 image
    for (const img of Array.from(base64Images)) {
      const base64Data = img.getAttribute('src');
      if (base64Data) {
        try {
          // Convert base64 to blob
          const response = await fetch(base64Data);
          const blob = await response.blob();
          const file = new File([blob], 'editor-image.png', { type: blob.type });

          // Upload the image
          const uploadResponse = await uploadBlogImage(file);
          if (uploadResponse?.data?.url) {
            // Replace base64 with uploaded URL
            img.setAttribute('src', uploadResponse.data.url);
          }
        } catch (error) {
          console.error('Error processing base64 image:', error);
          // Keep the base64 image if upload fails
        }
      }
    }

    return tempDiv.innerHTML;
  };

  const getRecords = useCallback(async (
    pageSize: number,
    pageIndex: number,
    search: string
  ) => {
    try {
      setLoading(true);
      const skip = pageIndex * pageSize;
      const limit = pageSize;
      
      // Try different search parameter names
      const filters: any = {
        skip: skip,
        limit: limit,
        search: search,  // Try this first (most common)
        // searchStr: search,  // Your original - comment out others to test
        // query: search,      // Alternative
        // keyword: search,    // Alternative
        // q: search,         // Alternative
        sortBy: 'createdAt desc'
      };

      console.log('🔍 Sending search request with filters:', filters);

      const res = await listingBlogs(filters);
      const blogsList = res?.data?.items || [];
      
      console.log('📄 Search response received:', {
        searchTerm: search,
        resultsCount: blogsList.length,
        totalCount: res?.data?.count,
        fullResponse: res
      });
      
      const blogTotalCount = res?.data?.count || 0;
      const calculatedTotalPages = Math.ceil(blogTotalCount / pageSize);
      
      // Extract unique categories from all blogs
      const allCategories = new Set<string>();
      blogsList.forEach((blog: Blog) => {
        if (blog.category && blog.category.trim()) {
          allCategories.add(blog.category.trim());
        }
      });
      setExistingCategories(Array.from(allCategories));
      
      setTotalCountOfPage(blogTotalCount);
      setTotalPages(calculatedTotalPages);
      setBlogs(blogsList);
      return blogsList;
    } catch (err: any) {
      console.error('❌ Error fetching blogs:', err);
      toast.error(err?.message || err?.msg || "Error fetching blogs");
      return [];
    } finally {
      setLoading(false);
    }
  }, [listingBlogs]);

  useEffect(() => {
    getRecords(pageSize, blogPageIndex, search);
  }, [getRecords, blogPageIndex, pageSize, search]);

  const handleNextPage = () => {
    setBlogPageIndex((prevIndex) => {
      const newIndex = Math.min(prevIndex + 1, totalPages - 1);
      const newSerialNumber = newIndex * pageSize + 1;
      setSerialNumber(newSerialNumber);
      return newIndex;
    });
  };

  const handlePreviousPage = () => {
    setBlogPageIndex((prevIndex) => {
      const newIndex = Math.max(prevIndex - 1, 0);
      const newSerialNumber = newIndex * pageSize + 1;
      setSerialNumber(newSerialNumber);
      return newIndex;
    });
  };

  const toggle = useCallback(() => {
    setModal(!modal);
  }, [modal]);

  const toggleDeleteModal = useCallback(() => {
    setDeleteModal(!deleteModal);
  }, [deleteModal]);

  // Keywords handlers
  const addKeyword = () => {
    if (keywordInput.trim() === "") return;
    
    const trimmedKeyword = keywordInput.trim();
    if (keywords.includes(trimmedKeyword)) {
      toast.error("This keyword already exists");
      return;
    }
    
    if (keywords.length >= 10) {
      toast.error("Maximum 10 keywords allowed");
      return;
    }
    
    setKeywords([...keywords, trimmedKeyword]);
    setKeywordInput("");
  };

  const removeKeyword = (index: number) => {
    const updatedKeywords = [...keywords];
    updatedKeywords.splice(index, 1);
    setKeywords(updatedKeywords);
  };

  const handleKeywordKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addKeyword();
    }
  };

  // Categories handlers
  const addCategory = () => {
    if (categoryInput.trim() === "") return;
    
    const trimmedCategory = categoryInput.trim();
    if (categories.includes(trimmedCategory)) {
      toast.error("This category already exists");
      return;
    }
    
    setCategories([...categories, trimmedCategory]);
    setCategoryInput("");
  };

  const removeCategory = (index: number) => {
    const updatedCategories = [...categories];
    updatedCategories.splice(index, 1);
    setCategories(updatedCategories);
  };

  const handleCategoryKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCategory();
    }
  };

  const addFaq = () => {
    if (faqs.length >= 10) {
      toast.error("Maximum 10 FAQs allowed");
      return;
    }
    setFaqs([...faqs, { question: "", answer: "" }]);
  };

  const removeFaq = (index: number) => {
    if (faqs.length <= 1) {
      toast.error("At least one FAQ field must remain");
      return;
    }
    const updatedFaqs = [...faqs];
    updatedFaqs.splice(index, 1);
    setFaqs(updatedFaqs);
  };

  const updateFaq = (index: number, field: string, value: string) => {
    const updatedFaqs = [...faqs];
    updatedFaqs[index][field] = value;
    setFaqs(updatedFaqs);
  };

  const onFeatureImageSelected = (event: any) => {
    const file: File = event.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPEG, PNG, GIF, WebP)');
        return;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error('Image size should not exceed 5MB');
        return;
      }

      setFeatureImageFile(file);

      const reader = new FileReader();
      reader.onload = (e: any) => {
        setFeatureImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFeatureImage = () => {
    setFeatureImageFile(null);
    setFeatureImagePreview(null);
  };

  const onGridImageSelected = (event: any) => {
    const file: File = event.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPEG, PNG, GIF, WebP)');
        return;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error('Image size should not exceed 5MB');
        return;
      }

      setGridImageFile(file);

      const reader = new FileReader();
      reader.onload = (e: any) => {
        setGridImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeGridImage = () => {
    setGridImageFile(null);
    setGridImagePreview(null);
  };

  const handleBlogContentChange = (content: string) => {
    setBlogContent(content);
  };

  useEffect(() => {
    if (modal && !isEdit) {
      setCurrentBlog(null);
      setFaqs([{ question: "", answer: "" }]);
      setFeatureImageFile(null);
      setFeatureImagePreview(null);
      setGridImageFile(null);
      setGridImagePreview(null);
      setBlogContent("");
      setKeywords([]);
      setCategories([]);
      setCategoryInput("");
      setKeywordInput("");
      validation.resetForm();
    }
  }, [modal]);

  const handleBlogClicks = () => {
    setCurrentBlog(null);
    setIsEdit(false);
    setIsEditMode(false);
    setEditingRecordId(null);
    setFaqs([{ question: "", answer: "" }]);
    setFeatureImageFile(null);
    setFeatureImagePreview(null);
    setGridImageFile(null);
    setGridImagePreview(null);
    setBlogContent("");
    setKeywords([]);
    setCategories([]);
    setCategoryInput("");
    setKeywordInput("");
    validation.resetForm();
    toggle();
  };

  // Handle search input change
  const handleSearchInputChange = (event: any) => {
    setSearchInput(event.target.value);
  };

  // Handle search submission (button click or enter key)
  const handleSearchSubmit = () => {
    console.log('🔍 Search submitted:', searchInput);
    setSearch(searchInput);
    setBlogPageIndex(0);
    setSerialNumber(1);
  };

  // Handle Enter key press in search input
  const handleSearchKeyPress = (event: any) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setBlogPageIndex(0);
    setSerialNumber(1);
  };

  const validation: any = useFormik({
    enableReinitialize: true,

    initialValues: {
      blogTitle: blogObject?.blogTitle || "",
      subtitle: blogObject?.subtitle || "",
      slug: blogObject?.slug || "",
      metaTitle: blogObject?.metaTitle || "",
      metaDescription: blogObject?.metaDescription || "",
      author: blogObject?.author || "",
      publishedDate: blogObject?.publishedDate ? moment(blogObject.publishedDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
      status: blogObject?.status || 'publish',
      featured: blogObject?.featured || false,
      category: blogObject?.category || "",
    },
    validationSchema: Yup.object({
      blogTitle: Yup.string()
        .required("Please Enter Blog Title")
        .min(5, "Blog title must be at least 5 characters")
        .max(200, "Blog title must be at most 200 characters"),
      subtitle: Yup.string()
        .max(300, "Subtitle must be at most 300 characters"),
      slug: Yup.string()
        .required("Please Enter Slug")
        .min(3, "Slug must be at least 3 characters")
        .matches(/^[a-z0-9-]+$/, "Slug must contain only lowercase letters, numbers, and hyphens"),
      metaTitle: Yup.string()
        .required("Please Enter Meta Title")
        .min(10, "Meta title must be at least 10 characters")
        .max(60, "Meta title must be at most 60 characters"),
      metaDescription: Yup.string()
        .required("Please Enter Meta Description")
        .min(120, "Meta description must be at least 120 characters")
        .max(500, "Meta description must be at most 500 characters"),
      author: Yup.string()
        .required("Please Enter Author Name")
        .min(2, "Author name must be at least 2 characters"),
      publishedDate: Yup.date().required("Please Select Publish Date"),
      status: Yup.string()
        .oneOf(['publish', 'unpublish'], "Status must be either publish or unpublish")
        .required("Please Select Status"),
      featured: Yup.boolean(),
      category: Yup.string()
        .max(100, "Category must be at most 100 characters"),
    }),
    onSubmit: async (values: any) => {
      if (!blogContent || blogContent.trim().length < 100) {
        toast.error('Blog content must be at least 100 characters');
        return;
      }

      if (!isEditMode && !featureImageFile) {
        toast.error('Please select a feature image');
        return;
      }

      setIsSubmitting(true);
      setLoading(true);

      try {
        let featureImageUrl = blogObject?.featureImage || '';
        let gridImageUrl = blogObject?.gridImage || '';

        // Upload feature image if a new one is selected
        if (featureImageFile) {
          const uploadResponse = await uploadBlogImage(featureImageFile);
          if (uploadResponse?.data?.url) {
            featureImageUrl = uploadResponse.data.url;
          }
        }

        // Upload grid image if a new one is selected
        if (gridImageFile) {
          const uploadResponse = await uploadBlogImage(gridImageFile);
          if (uploadResponse?.data?.url) {
            gridImageUrl = uploadResponse.data.url;
          }
        }

        // Process blog content to convert base64 images to uploaded URLs
        let processedBlogContent = blogContent;
        if (blogContent.includes('data:image/')) {
          processedBlogContent = await processBlogContentImages(blogContent);
        }

        const filteredFaqs = faqs.filter(faq => 
          faq.question?.trim() && faq.answer?.trim()
        );

        const blogData: any = {
          blogTitle: values.blogTitle.trim(),
          subtitle: values.subtitle?.trim() || '',
          metaTitle: values.metaTitle.trim(),
          slug: values.slug.trim(),
          metaDescription: values.metaDescription.trim(),
          blogContent: processedBlogContent,
          featureImage: featureImageUrl,
          gridImage: gridImageUrl,
          author: values.author.trim(),
          publishedDate: new Date(values.publishedDate).toISOString(),
          status: values.status,
          featured: values.featured,
          keywords: keywords.length > 0 ? keywords : [],
          category: values.category?.trim() || (categories.length > 0 ? categories[0] : ''),
          faq: filteredFaqs.length > 0 ? filteredFaqs : undefined,
          tags: blogObject?.tags || [],
          active: true,
          isArchived: false,
        };

        let response;
        if (isEditMode && blogObject?._id) {
          blogData._id = blogObject._id;
          response = await updateBlog(blogData);
        } else {
          response = await createBlog(blogData);
        }

        if (response) {
          toast.success(isEditMode ? 'Blog updated successfully' : 'Blog created successfully');
          await getRecords(pageSize, blogPageIndex, search);
          setModal(false);
          validation.resetForm();
          setFaqs([{ question: "", answer: "" }]);
          setFeatureImageFile(null);
          setFeatureImagePreview(null);
          setGridImageFile(null);
          setGridImagePreview(null);
          setBlogContent("");
          setKeywords([]);
          setCategories([]);
          setIsEditMode(false);
          setEditingRecordId(null);
          setCurrentBlog(null);
        }
      } catch (err: any) {
        console.error('Error saving blog:', err);
        toast.error(err?.message || err?.msg || `Error ${isEditMode ? 'updating' : 'creating'} blog`);
      } finally {
        setIsSubmitting(false);
        setLoading(false);
      }
    },
  });

  const handleBlogClick = useCallback(
    (arg: Blog) => {
      setCurrentBlog(arg);
      setIsEdit(true);
      setIsEditMode(true);
      setEditingRecordId(arg._id || null);
      setFeatureImagePreview(arg.featureImage || null);
      setGridImagePreview(arg.gridImage || null);
      
      setBlogContent(arg.blogContent || "");
      
      // Set keywords from blog object
      if (arg.keywords && Array.isArray(arg.keywords)) {
        setKeywords(arg.keywords);
      } else {
        setKeywords([]);
      }
      
      // Set categories from blog object
      if (arg.category) {
        setCategories([arg.category]);
        validation.setFieldValue('category', arg.category);
      } else {
        setCategories([]);
        validation.setFieldValue('category', '');
      }
      
      if (arg.faq && arg.faq.length > 0) {
        setFaqs(arg.faq);
      } else {
        setFaqs([{ question: "", answer: "" }]);
      }
      
      toggle();
    },
    [toggle, validation]
  );

  const handleDeleteClick = useCallback(
    (arg: Blog) => {
      setCurrentBlog(arg);
      toggleDeleteModal();
    },
    [toggleDeleteModal]
  );

  const confirmDelete = async () => {
    if (!blogObject?._id) return;
    
    try {
      setLoading(true);
      await deleteBlog({ _id: blogObject._id });
      await getRecords(pageSize, blogPageIndex, search);
      toggleDeleteModal();
      toast.success("Blog deleted successfully!");
      setCurrentBlog(null);
    } catch (err: any) {
      console.error('Error deleting blog:', err);
      toast.error(err?.message || err?.msg || "Error deleting blog");
    } finally {
      setLoading(false);
    }
  };

  const handleValidDate = (date: any) => {
    const date1 = moment(new Date(date)).format("DD-MM-YYYY");
    return date1;
  };

  const columns = useMemo(
    () => [
      {
        Header: "No.",
        accessor: (row: any, index: any) => index + 1 + blogPageIndex * pageSize,
        id: "serialNumberColumn",
        filterable: false,
        width: 60,
      },
      {
        Header: "Title",
        accessor: "blogTitle",
        filterable: false,
        Cell: (blogs: any) => (
          <>
            <div className="d-flex align-items-center">
              <div>
                {blogs.row.original.blogTitle && blogs.row.original.blogTitle.length > 40 
                  ? blogs.row.original.blogTitle.substring(0, 40) + '...'
                  : blogs.row.original.blogTitle
                }
                {blogs.row.original.subtitle && (
                  <div className="text-muted small mt-1">
                    {blogs.row.original.subtitle.length > 50 
                      ? blogs.row.original.subtitle.substring(0, 50) + '...'
                      : blogs.row.original.subtitle
                    }
                  </div>
                )}
              </div>
            </div>
          </>
        ),
      },
      {
        Header: "Slug",
        accessor: "slug",
        filterable: false,
        Cell: (blogs: any) => (
          <>
            <div className="d-flex align-items-center">
              <div>
                {blogs.row.original.slug && blogs.row.original.slug.length > 40 
                  ? blogs.row.original.slug.substring(0, 40) + '...'
                  : blogs.row.original.slug || '-'
                }
              </div>
            </div>
          </>
        ),
      },
      {
        Header: "Category",
        accessor: "category",
        filterable: false,
        Cell: (cell: any) => {
          const category = cell.row.original.category;
          return category ? (
            <Badge color="info" className="fs-12">
              {category}
            </Badge>
          ) : '-';
        },
      },
      {
        Header: "Status",
        accessor: "status",
        filterable: false,
        Cell: (cell: any) => {
          const status = cell.row.original.status;
          const isPublished = status === 'publish';
          
          return (
            <span 
              className={`badge ${isPublished ? 'bg-success' : 'bg-secondary'} fs-12`}
              style={{
                padding: '0.35em 0.65em',
                fontSize: '0.75em',
                fontWeight: '600'
              }}
            >
              {isPublished ? 'Published' : 'Unpublished'}
            </span>
          );
        },
      },
      {
        Header: "Featured",
        accessor: "featured",
        filterable: false,
        Cell: (cell: any) => {
          const isFeatured = cell.row.original.featured;
          return (
            <Badge color={isFeatured ? "warning" : "light"} className="fs-12">
              {isFeatured ? "Yes" : "No"}
            </Badge>
          );
        },
      },
      {
        Header: "Published At",
        accessor: "publishedDate",
        filterable: false,
        Cell: (cell: any) => (
          <>{cell.row.original.publishedDate ? handleValidDate(cell.row.original.publishedDate) : '-'}</>
        ),
      },
      {
        Header: "Author",
        accessor: "author",
        filterable: false,
      },
      {
        Header: "Action",
        Cell: (cellProps: any) => {
          return (
            <ul className="list-inline hstack mb-0">
              <li className="list-inline-item" title="Edit">
                <Link
                  className="text-success"
                  to="#"
                  onClick={() => {
                    const blogData = cellProps.row.original;
                    handleBlogClick(blogData);
                  }}
                >
                  <i className="mdi mdi-pencil font-size-18"></i>
                </Link>
              </li>
              <li className="list-inline-item" title="Delete">
                <Link
                  className="text-danger"
                  to="#"
                  onClick={() => {
                    const blogData = cellProps.row.original;
                    handleDeleteClick(blogData);
                  }}
                >
                  <i className="mdi mdi-delete font-size-18"></i>
                </Link>
              </li>
            </ul>
          );
        },
      },
    ],
    [handleBlogClick, handleDeleteClick, blogPageIndex, pageSize]
  );

  document.title = "Blogs";

  return (
    <React.Fragment>
      {loading && <SpinnerLoader />}
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Blogs" pageTitle="Blogs" pageLink="/blogs" />
          
          <Row>
            <Col lg={14}>
              <Card id="blogList">
                <CardHeader className="border-0">
                  <div className="d-md-flex align-items-center">
                    <h5 className="card-title mb-3 mb-md-0 flex-grow-1">
                      Blogs ({totalCountOfPage})
                    </h5>
                    <div className="flex-shrink-0">
                      <div className="d-flex gap-1 flex-wrap">
                        <button
                          type="button"
                          className="btn btn-success add-btn"
                          id="create-btn"
                          onClick={() => {
                            setIsEdit(false);
                            setIsEditMode(false);
                            setEditingRecordId(null);
                            setCurrentBlog(null);
                            setFaqs([{ question: "", answer: "" }]);
                            setFeatureImageFile(null);
                            setFeatureImagePreview(null);
                            setGridImageFile(null);
                            setGridImagePreview(null);
                            setBlogContent("");
                            setKeywords([]);
                            setCategories([]);
                            setCategoryInput("");
                            setKeywordInput("");
                            validation.resetForm();
                            toggle();
                          }}
                        >
                          <i className="ri-add-line align-bottom me-1"></i>{" "}
                          Add Blog
                        </button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardBody className="border border-dashed border-end-0 border-start-0">
                  <Row className="g-4 align-items-center">
                    <Col sm={8}>
                      <div className="search-box position-relative">
                        <Input
                          type="text"
                          className="form-control search pe-5"
                          placeholder="Search for blogs, slugs, categories, or authors..."
                          value={searchInput}
                          onChange={handleSearchInputChange}
                          onKeyPress={handleSearchKeyPress}
                        />
                        <i className="ri-search-line search-icon"></i>
                        
                        {/* Search and Clear buttons as icons inside the input */}
                        <div className="position-absolute top-50 end-0 translate-middle-y d-flex align-items-center me-2">
                          
                          <button
                            type="button"
                            className="btn btn-md btn-link text-primary p-0 me-2"
                            onClick={handleSearchSubmit}
                            disabled={loading}
                            title="Search"
                          >
                            <i className="ri-search-2-line font-size-16"></i>
                          </button>

                          {searchInput && (
                            <button
                              type="button"
                              className="btn btn-md btn-link text-muted p-0 me-1"
                              onClick={handleClearSearch}
                              disabled={loading}
                              title="Clear search"
                            >
                              <i className="ri-close-circle-fill font-size-16"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col sm={4}>
                      <div className="text-muted">
                        Showing {blogs.length} of {totalCountOfPage} blogs 
                        {search && (
                          <span> , search results for "{search}"</span>
                        )}
                      </div>
                    </Col>
                  </Row>
                </CardBody>

                <CardBody className="pt-0">
                  <div>
                    {blogs && blogs.length ? (
                      <TableContainerComp
                        columns={columns}
                        data={blogs || []}
                        isGlobalFilter={false}
                        isAddUserList={false}
                        customPageSize={10}
                        className="custom-header-css"
                        divClass="table-responsive table-card"
                        tableClass="align-middle"
                        theadClass="table-light text-muted"
                        handleLeadClick={handleBlogClicks}
                        isLeadsFilter={false}
                        totalCount={totalCountOfPage}
                        handleNextPage={handleNextPage}
                        handlePreviousPage={handlePreviousPage}
                        getRecords={() => getRecords(pageSize, blogPageIndex, search)}
                        setUser={setBlogs}
                        setUserPageIndex={setBlogPageIndex}
                        userPageIndex={blogPageIndex}
                      />
                    ) : (
                      <div className="text-center py-4">
                        <div className="fw-bold unavailable-msg">No Blogs Available</div>
                        <p className="mt-2 non-record-text">
                          {loading ? 'Loading blogs...' : search ? `No blogs found for "${search}"` : "It looks like you haven't set up any blogs yet. Add a blog to start sharing your content."}
                        </p>
                        {!loading && (
                          <button 
                            className="btn btn-success mt-3"
                            onClick={() => {
                              setIsEdit(false);
                              setIsEditMode(false);
                              setEditingRecordId(null);
                              setCurrentBlog(null);
                              setFaqs([{ question: "", answer: "" }]);
                              setFeatureImageFile(null);
                              setFeatureImagePreview(null);
                              setGridImageFile(null);
                              setGridImagePreview(null);
                              setBlogContent("");
                              setKeywords([]);
                              setCategories([]);
                              setCategoryInput("");
                              setKeywordInput("");
                              validation.resetForm();
                              toggle();
                            }}
                          >
                            Add a New Blog
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Add/Edit Blog Modal */}
                  <Modal
                    className="modal-xl"
                    id="showModal"
                    isOpen={modal}
                    toggle={toggle}
                    centered
                    size="xl"
                    style={{ maxWidth: '1200px' }}
                  >
                    <ModalHeader className="bg-light p-3" toggle={toggle}>
                      {isEditMode ? "EDIT BLOG" : "ADD BLOG"}
                    </ModalHeader>
                    <Form
                      className="tablelist-form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      <ModalBody style={{ maxHeight: '75vh', overflowY: 'auto', minHeight: '500px' }}>
                        <Row className="g-3">
                          {/* Blog Title & Subtitle */}
                          <Col md={6}>
                            <Label htmlFor="blogTitle" className="form-label">
                              Blog Title *
                            </Label>
                            <Input
                              name="blogTitle"
                              id="blogTitle"
                              className="form-control"
                              placeholder="Enter blog title"
                              type="text"
                              onChange={handleBlogTitleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.blogTitle || ""}
                              invalid={
                                validation.touched.blogTitle &&
                                validation.errors.blogTitle
                              }
                              disabled={isSubmitting}
                            />
                            {validation.touched.blogTitle &&
                              validation.errors.blogTitle ? (
                              <FormFeedback type="invalid">
                                {validation.errors.blogTitle}
                              </FormFeedback>
                            ) : null}
                          </Col>
                          <Col md={6}>
                            <Label htmlFor="subtitle" className="form-label">
                              Subtitle
                            </Label>
                            <Input
                              name="subtitle"
                              id="subtitle"
                              className="form-control"
                              placeholder="Enter subtitle (optional)"
                              type="text"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.subtitle || ""}
                              invalid={
                                validation.touched.subtitle &&
                                validation.errors.subtitle
                              }
                              disabled={isSubmitting}
                            />
                            {validation.touched.subtitle &&
                              validation.errors.subtitle ? (
                              <FormFeedback type="invalid">
                                {validation.errors.subtitle}
                              </FormFeedback>
                            ) : null}
                          </Col>

                          {/* Slug & Featured */}
                          <Col md={6}>
                            <Label htmlFor="slug" className="form-label">
                              Slug *
                            </Label>
                            <Input
                              name="slug"
                              id="slug"
                              className="form-control"
                              placeholder="Enter slug (URL friendly)"
                              type="text"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.slug || ""}
                              invalid={
                                validation.touched.slug &&
                                validation.errors.slug
                              }
                              disabled={isSubmitting}
                            />
                            {validation.touched.slug &&
                              validation.errors.slug ? (
                              <FormFeedback type="invalid">
                                {validation.errors.slug}
                              </FormFeedback>
                            ) : null}
                          </Col>
                          <Col md={6}>
                            <div className="form-check form-switch mt-4">
                              <Input
                                name="featured"
                                id="featured"
                                type="checkbox"
                                className="form-check-input"
                                checked={validation.values.featured}
                                onChange={(e) => validation.setFieldValue('featured', e.target.checked)}
                                disabled={isSubmitting}
                              />
                              <Label className="form-check-label" htmlFor="featured">
                                Mark as Featured
                              </Label>
                            </div>
                          </Col>

                          {/* Meta Title & Author */}
                          <Col md={6}>
                            <Label htmlFor="metaTitle" className="form-label">
                              Meta Title *
                            </Label>
                            <Input
                              name="metaTitle"
                              id="metaTitle"
                              className="form-control"
                              placeholder="Enter meta title"
                              type="text"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.metaTitle || ""}
                              invalid={
                                validation.touched.metaTitle &&
                                validation.errors.metaTitle
                              }
                              disabled={isSubmitting}
                            />
                            {validation.touched.metaTitle &&
                              validation.errors.metaTitle ? (
                              <FormFeedback type="invalid">
                                {validation.errors.metaTitle}
                              </FormFeedback>
                            ) : null}
                          </Col>
                          <Col md={6}>
                            <Label htmlFor="author" className="form-label">
                              Author Name *
                            </Label>
                            <Input
                              name="author"
                              id="author"
                              className="form-control"
                              placeholder="Enter author name"
                              type="text"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.author || ""}
                              invalid={
                                validation.touched.author &&
                                validation.errors.author
                              }
                              disabled={isSubmitting}
                            />
                            {validation.touched.author &&
                              validation.errors.author ? (
                              <FormFeedback type="invalid">
                                {validation.errors.author}
                              </FormFeedback>
                            ) : null}
                          </Col>

                          {/* Meta Description */}
                          <Col lg={12}>
                            <Label htmlFor="metaDescription" className="form-label">
                              Meta Description *
                            </Label>
                            <Input
                              name="metaDescription"
                              id="metaDescription"
                              className="form-control"
                              placeholder="Enter meta description"
                              type="textarea"
                              rows="3"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.metaDescription || ""}
                              invalid={
                                validation.touched.metaDescription &&
                                validation.errors.metaDescription
                              }
                              disabled={isSubmitting}
                            />
                            {validation.touched.metaDescription &&
                              validation.errors.metaDescription ? (
                              <FormFeedback type="invalid">
                                {validation.errors.metaDescription}
                              </FormFeedback>
                            ) : null}
                          </Col>

                          {/* Publish Date, Status & Category */}
                          <Col md={4}>
                            <Label htmlFor="publishedDate" className="form-label">
                              Publish Date *
                            </Label>
                            <Input
                              name="publishedDate"
                              id="publishedDate"
                              className="form-control"
                              type="date"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.publishedDate ? moment(validation.values.publishedDate).format('YYYY-MM-DD') : ''}
                              invalid={
                                validation.touched.publishedDate &&
                                validation.errors.publishedDate
                              }
                              disabled={isSubmitting}
                            />
                            {validation.touched.publishedDate &&
                              validation.errors.publishedDate ? (
                              <FormFeedback type="invalid">
                                {validation.errors.publishedDate}
                              </FormFeedback>
                            ) : null}
                          </Col>

                          <Col md={4}>
                            <Label htmlFor="status" className="form-label">
                              Status *
                            </Label>
                            <Input
                              name="status"
                              id="publish-status"
                              className="form-control"
                              type="select"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.status || ""}
                              invalid={
                                validation.touched.status &&
                                validation.errors.status
                              }
                              disabled={isSubmitting}
                            >
                              <option value="publish">Publish</option>
                              <option value="unpublish">Unpublish</option>
                            </Input>
                            {validation.touched.status &&
                              validation.errors.status ? (
                              <FormFeedback type="invalid">
                                {validation.errors.status}
                              </FormFeedback>
                            ) : null}
                          </Col>

                          <Col md={4}>
                            <Label htmlFor="category" className="form-label">
                              Category
                            </Label>
                            <Input
                              name="category"
                              id="category"
                              className="form-control"
                              type="text"
                              list="existingCategories"
                              placeholder="Select or type a category"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.category || ""}
                              invalid={
                                validation.touched.category &&
                                validation.errors.category
                              }
                              disabled={isSubmitting}
                            />
                            <datalist id="existingCategories">
                              {existingCategories.map((cat, index) => (
                                <option key={index} value={cat} />
                              ))}
                            </datalist>
                            {validation.touched.category &&
                              validation.errors.category ? (
                              <FormFeedback type="invalid">
                                {validation.errors.category}
                              </FormFeedback>
                            ) : null}
                          </Col>

                          {/* Feature Image Upload */}
                          <Col lg={6}>
                            <Label htmlFor="featureImageInput" className="form-label">
                              Feature Image {isEditMode ? '' : '*'}
                            </Label>
                            <div className="card border">
                              <div className="card-body">
                                {!featureImagePreview ? (
                                  <div className="text-center py-4">
                                    <Input 
                                      type="file" 
                                      id="featureImageInput" 
                                      className="d-none" 
                                      accept="image/*"
                                      onChange={onFeatureImageSelected}
                                      disabled={isSubmitting}
                                    />
                                    <div className="mb-3">
                                      <i className="ri-image-line" style={{ fontSize: '48px' }}></i>
                                    </div>
                                    <p className="mb-2">Drag and drop your feature image here or click to browse</p>
                                    <small className="text-muted">Supported formats: JPEG, PNG, GIF, WebP (Max 5MB)</small>
                                    <br />
                                    <button 
                                      type="button" 
                                      className="btn btn-primary btn-sm mt-2" 
                                      onClick={() => document.getElementById('featureImageInput')?.click()}
                                      disabled={isSubmitting}
                                    >
                                      Choose Feature Image
                                    </button>
                                  </div>
                                ) : (
                                  <div className="text-center">
                                    <img 
                                      src={featureImagePreview} 
                                      alt="Preview" 
                                      className="img-fluid rounded"
                                      style={{ maxWidth: '100%', maxHeight: '250px' }} 
                                    />
                                    <div className="mt-3">
                                      <button 
                                        type="button" 
                                        className="btn btn-danger btn-sm me-2" 
                                        onClick={removeFeatureImage}
                                        disabled={isSubmitting}
                                      >
                                        Remove Image
                                      </button>
                                      <button 
                                        type="button" 
                                        className="btn btn-primary btn-sm" 
                                        onClick={() => document.getElementById('featureImageInput')?.click()}
                                        disabled={isSubmitting}
                                      >
                                        Change Image
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </Col>

                          {/* Grid Image Upload */}
                          <Col lg={6}>
                            <Label htmlFor="gridImageInput" className="form-label">
                              Grid Image (Optional)
                            </Label>
                            <div className="card border">
                              <div className="card-body">
                                {!gridImagePreview ? (
                                  <div className="text-center py-4">
                                    <Input 
                                      type="file" 
                                      id="gridImageInput" 
                                      className="d-none" 
                                      accept="image/*"
                                      onChange={onGridImageSelected}
                                      disabled={isSubmitting}
                                    />
                                    <div className="mb-3">
                                      <i className="ri-image-line" style={{ fontSize: '48px' }}></i>
                                    </div>
                                    <p className="mb-2">Drag and drop your grid image here or click to browse</p>
                                    <small className="text-muted">Supported formats: JPEG, PNG, GIF, WebP (Max 5MB)</small>
                                    <br />
                                    <button 
                                      type="button" 
                                      className="btn btn-primary btn-sm mt-2" 
                                      onClick={() => document.getElementById('gridImageInput')?.click()}
                                      disabled={isSubmitting}
                                    >
                                      Choose Grid Image
                                    </button>
                                  </div>
                                ) : (
                                  <div className="text-center">
                                    <img 
                                      src={gridImagePreview} 
                                      alt="Preview" 
                                      className="img-fluid rounded"
                                      style={{ maxWidth: '100%', maxHeight: '250px' }} 
                                    />
                                    <div className="mt-3">
                                      <button 
                                        type="button" 
                                        className="btn btn-danger btn-sm me-2" 
                                        onClick={removeGridImage}
                                        disabled={isSubmitting}
                                      >
                                        Remove Image
                                      </button>
                                      <button 
                                        type="button" 
                                        className="btn btn-primary btn-sm" 
                                        onClick={() => document.getElementById('gridImageInput')?.click()}
                                        disabled={isSubmitting}
                                      >
                                        Change Image
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </Col>

                          {/* Keywords */}
                          <Col lg={12}>
                            <Label className="form-label">
                              Keywords
                            </Label>
                            <div className="card border">
                              <div className="card-body">
                                <div className="d-flex mb-3">
                                  <Input
                                    type="text"
                                    className="form-control me-2"
                                    placeholder="Enter a keyword and press Enter or click Add"
                                    value={keywordInput}
                                    onChange={(e) => setKeywordInput(e.target.value)}
                                    onKeyPress={handleKeywordKeyPress}
                                    disabled={isSubmitting}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={addKeyword}
                                    disabled={isSubmitting || keywordInput.trim() === ""}
                                  >
                                    Add
                                  </button>
                                </div>
                                
                                {keywords.length > 0 ? (
                                  <div className="d-flex flex-wrap gap-2">
                                    {keywords.map((keyword, index) => (
                                      <Badge key={index} color="primary" className="p-2 d-flex align-items-center">
                                        {keyword}
                                        <button
                                          type="button"
                                          className="btn-close btn-close-white ms-2"
                                          onClick={() => removeKeyword(index)}
                                          disabled={isSubmitting}
                                          style={{ fontSize: '10px' }}
                                        />
                                      </Badge>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-muted text-center py-2">
                                    No keywords added yet. Add keywords to help with search engine optimization.
                                  </div>
                                )}
                                <small className="text-muted mt-2 d-block">
                                  Maximum 10 keywords allowed. Keywords help users find your blog through search.
                                </small>
                              </div>
                            </div>
                          </Col>

                          {/* Blog Content - Rich Text Editor */}
                          <Col lg={12}>
                            <Label htmlFor="blogContent" className="form-label">
                              Blog Content *
                            </Label>
                            <div className="text-editor-container">
                              <ReactQuill
                                value={blogContent}
                                onChange={handleBlogContentChange}
                                modules={modules}
                                formats={formats}
                                placeholder="Write your blog content here..."                               
                              />
                            </div>
                            {blogContent && blogContent.trim().length < 100 && (
                              <div className="text-danger small mt-1">
                                Blog content must be at least 100 characters (currently {blogContent.trim().length})
                              </div>
                            )}
                          </Col>

                          {/* FAQs Section */}
                          <Col lg={12}>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <Label className="form-label mb-0">FAQs</Label>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={addFaq}
                                disabled={isSubmitting}
                              >
                                <i className="ri-add-line align-bottom me-1"></i> Add FAQ
                              </button>
                            </div>
                            
                            {faqs.map((faq, index) => (
                              <div key={index} className="card border mb-3">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                  <span>FAQ {index + 1}</span>
                                  {faqs.length > 1 && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger"
                                      onClick={() => removeFaq(index)}
                                      disabled={isSubmitting}
                                    >
                                      <i className="ri-delete-bin-line align-bottom"></i>
                                    </button>
                                  )}
                                </div>
                                <div className="card-body">
                                  <Row>
                                    <Col md={6}>
                                      <Label className="form-label">Question</Label>
                                      <Input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter question"
                                        value={faq.question}
                                        onChange={(e) => updateFaq(index, 'question', e.target.value)}
                                        disabled={isSubmitting}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Label className="form-label">Answer</Label>
                                      <Input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter answer"
                                        value={faq.answer}
                                        onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                                        disabled={isSubmitting}
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              </div>
                            ))}
                          </Col>
                        </Row>
                      </ModalBody>
                      <ModalFooter>
                        <div className="hstack gap-2 justify-content-end">
                          <button
                            type="button"
                            className="btn btn-light"
                            onClick={() => {
                              setModal(false);
                              validation.resetForm();
                            }}
                            disabled={isSubmitting}
                          >
                            Close
                          </button>
                          <button
                            type="submit"
                            className="btn btn-success"
                            disabled={isSubmitting || !blogContent || blogContent.trim().length < 100 || (!isEditMode && !featureImageFile)}
                          >
                            {isSubmitting ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {isEditMode ? "Updating..." : "Creating..."}
                              </>
                            ) : (
                              <>{isEditMode ? "Update Blog" : "Create Blog"}</>
                            )}
                          </button>
                        </div>
                      </ModalFooter>
                    </Form>
                  </Modal>

                  {/* Delete Modal */}
                  <Modal
                    isOpen={deleteModal}
                    toggle={toggleDeleteModal}
                    centered
                  >
                    <ModalHeader toggle={toggleDeleteModal}>
                      Delete Blog
                    </ModalHeader>
                    <ModalBody>
                      <div className="text-center">
                        <i className="ri-alert-fill text-danger" style={{ fontSize: '48px' }}></i>
                        <h4 className="mt-3">Are you sure?</h4>
                        <p className="text-muted">
                          You are about to delete the blog "{blogObject?.blogTitle}". This action cannot be undone.
                        </p>
                      </div>
                    </ModalBody>
                    <ModalFooter>
                      <div className="hstack gap-2 justify-content-center">
                        <button
                          type="button"
                          className="btn btn-light"
                          onClick={toggleDeleteModal}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={confirmDelete}
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Deleting...
                            </>
                          ) : (
                            "Delete Blog"
                          )}
                        </button>
                      </div>
                    </ModalFooter>
                  </Modal>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default FluentBlog;